import { Chart, registerables } from 'chart.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  categoryColor,
  CLASS_COLORS,
  classStacksOverTime,
  fetchFleetCompositionBundle,
  fuelLabel,
  FUEL_COLORS,
  fuelStacksOverTime,
  type FleetCompositionBundle,
  type FleetCompositionMetrics,
  vehicleCategoryLabel,
} from '../../services/fleetCompositionService';
import { downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';
import {
  ChartLayout,
  MethodologyContent,
  OverviewContent,
} from './ChartLayout';
import {
  buildFleetCompositionAuthoredBundle,
  buildFleetCompositionKeyInsights,
  fleetCompositionOverviewDescription,
  fleetCompositionStructuredMethodology,
  resolveFleetNarrativeViewId,
} from '../../domains/narrative/fleetCompositionNarrative';
import {
  buildFleetClassCategoryFacts,
  buildFleetDutyClassFacts,
  buildFleetFuelFacts,
} from '../../domains/narrative/fleetCompositionFacts';
import { AuthoredEquityPanel, AuthoredRelevancePanel } from '../../domains/narrative/narrativeSectionContent';
import { resolveMethodologyContentProps } from '../../domains/narrative/methodologyContentProps';
import { KeyMetric, KeyMetrics } from './KeyMetrics';
import './ChartLayout.css';

Chart.register(...registerables);

export type FleetCompositionChartView = 'intensity' | 'by_class' | 'by_fuel';

interface Props {
  location: string;
  geofips: number | null;
  chartView?: FleetCompositionChartView;
  onChartViewChange?: (view: FleetCompositionChartView) => void;
  onMetricsCalculated?: (m: FleetCompositionMetrics | null) => void;
}

function fmtInt(n: number): string {
  return Math.round(n).toLocaleString();
}

function fmtOne(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 1, minimumFractionDigits: 0 });
}

function signedPct(n: number): string {
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

export const FleetCompositionSection: React.FC<Props> = ({
  location,
  geofips,
  chartView: controlledView,
  onChartViewChange,
  onMetricsCalculated,
}) => {
  const [internalView, setInternalView] = useState<FleetCompositionChartView>('by_fuel');
  const chartView = controlledView ?? internalView;
  const setChartView = (v: FleetCompositionChartView) => {
    onChartViewChange?.(v);
    if (controlledView == null) setInternalView(v);
  };

  const [activePanel, setActivePanel] = useState('overview');
  const [bundle, setBundle] = useState<FleetCompositionBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [classMode, setClassMode] = useState<'vehicle_category' | 'class'>('vehicle_category');
  const [fuelScope, setFuelScope] = useState<'all' | 'LDV'>('LDV');

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  const metricsCbRef = useRef(onMetricsCalculated);
  useEffect(() => {
    metricsCbRef.current = onMetricsCalculated;
  }, [onMetricsCalculated]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchFleetCompositionBundle(location, geofips);
        if (cancelled) return;
        setBundle(data);
        metricsCbRef.current?.(data?.metrics ?? null);
      } catch (e: unknown) {
        if (!cancelled) {
          setBundle(null);
          setError(e instanceof Error ? e.message : 'Failed to load fleet composition');
          metricsCbRef.current?.(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (location && geofips != null) load();
    else {
      setLoading(false);
      setBundle(null);
    }
    return () => {
      cancelled = true;
    };
  }, [location, geofips]);

  const metrics = bundle?.metrics ?? null;

  const narrativeViewId = resolveFleetNarrativeViewId(chartView, fuelScope, classMode);

  const authoredBundle = useMemo(
    () => buildFleetCompositionAuthoredBundle(location, bundle, chartView, fuelScope, classMode),
    [bundle, chartView, classMode, fuelScope, location]
  );
  const keyInsightsText = useMemo(
    () =>
      bundle
        ? buildFleetCompositionKeyInsights(location, bundle, chartView, fuelScope, classMode)
        : `Loading vehicle fleet composition for ${location}...`,
    [bundle, chartView, classMode, fuelScope, location]
  );

  const fleetKeyMetrics = useMemo(() => {
    if (!metrics || !bundle) return null;
    if (chartView === 'intensity') {
      return (
        <KeyMetrics>
          <KeyMetric
            value={fmtOne(metrics.ldvGPerMileLatest)}
            label={`Modeled LDV gCO₂e/mile (${metrics.latestYear})`}
            color="#1e3a8a"
          />
          <KeyMetric
            value={signedPct(metrics.ldvGPerMilePctChange)}
            label={`Change since ${metrics.baselineYear}`}
            color={metrics.ldvGPerMilePctChange <= 0 ? '#10b981' : '#dc2626'}
          />
          <KeyMetric
            value={
              metrics.caLdvGPerMileLatest != null ? fmtOne(metrics.caLdvGPerMileLatest) : '—'
            }
            label={`California LDV gCO₂e/mile (${metrics.latestYear})`}
            color="#f59e0b"
          />
          <KeyMetric
            value={
              metrics.caLdvGPerMileLatest != null
                ? fmtOne(metrics.ldvGPerMileLatest - metrics.caLdvGPerMileLatest)
                : '—'
            }
            label="Difference from California"
            color="#1e3a8a"
          />
        </KeyMetrics>
      );
    }
    if (chartView === 'by_fuel') {
      const scope = fuelScope;
      const facts = buildFleetFuelFacts(location, bundle, scope);
      return (
        <KeyMetrics className="key-metrics-four">
          <KeyMetric
            value={fmtInt(facts.displayedFleetTotal as number)}
            label={`Fleet total (${scope === 'LDV' ? 'LDV' : 'all classes'}, ${metrics.latestYear})`}
          />
          <KeyMetric
            value={facts.dominantShare != null ? `${facts.dominantShare}%` : '—'}
            label={facts.dominantFleetCategory ? `Largest: ${facts.dominantFleetCategory}` : 'Dominant fuel'}
          />
          <KeyMetric
            value={facts.pluginShare != null ? `${facts.pluginShare}%` : '—'}
            label={`Plug-in share (${metrics.latestYear})`}
            color="#059669"
          />
          <KeyMetric
            value={
              facts.pluginSharePointChange != null
                ? `${facts.pluginSharePointChange > 0 ? '+' : ''}${facts.pluginSharePointChange.toFixed(1)} pts`
                : '—'
            }
            label={`Plug-in share change since ${metrics.baselineYear}`}
          />
        </KeyMetrics>
      );
    }
    if (chartView === 'by_class' && classMode === 'vehicle_category') {
      const facts = buildFleetClassCategoryFacts(location, bundle);
      return (
        <KeyMetrics>
          <KeyMetric value={fmtInt(facts.displayedFleetTotal as number)} label={`Fleet total (${metrics.latestYear})`} />
          <KeyMetric
            value={facts.dominantClassShare != null ? `${facts.dominantClassShare}%` : '—'}
            label={facts.dominantClassLabel ?? 'Largest category'}
          />
        </KeyMetrics>
      );
    }
    if (chartView === 'by_class') {
      const facts = buildFleetDutyClassFacts(location, bundle);
      return (
        <KeyMetrics>
          <KeyMetric value={fmtInt(facts.displayedFleetTotal as number)} label={`Fleet total (${metrics.latestYear})`} />
          <KeyMetric value={facts.ldvShare != null ? `${facts.ldvShare}%` : '—'} label="LDV share" />
          <KeyMetric value={facts.hdvShare != null ? `${facts.hdvShare}%` : '—'} label="HDV share" />
        </KeyMetrics>
      );
    }
    return null;
  }, [bundle, chartView, classMode, fuelScope, location, metrics]);

  useEffect(() => {
    if (!bundle || !canvasRef.current) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    if (chartView === 'intensity') {
      const years = Array.from(
        new Set([
          ...bundle.ldvIntensityLocation.map((d) => d.year),
          ...bundle.ldvIntensityCalifornia.map((d) => d.year),
        ])
      ).sort((a, b) => a - b);
      const locMap = new Map(bundle.ldvIntensityLocation.map((d) => [d.year, d.value]));
      const caMap = new Map(bundle.ldvIntensityCalifornia.map((d) => [d.year, d.value]));

      chartRef.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: years,
          datasets: [
            {
              label: location,
              data: years.map((y) => locMap.get(y) ?? null),
              borderColor: '#2563eb',
              backgroundColor: 'rgba(37, 99, 235, 0.12)',
              tension: 0.25,
              spanGaps: true,
              pointRadius: 3,
            },
            {
              label: 'California',
              data: years.map((y) => caMap.get(y) ?? null),
              borderColor: '#64748b',
              backgroundColor: 'rgba(100, 116, 139, 0.08)',
              borderDash: [6, 4],
              tension: 0.25,
              spanGaps: true,
              pointRadius: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: standardLegend,
            title: standardTitle('LDV carbon intensity (gCO₂e/mile)'),
            tooltip: {
              callbacks: {
                label: (item) => {
                  const v = item.parsed.y;
                  if (v == null || !Number.isFinite(v)) return `${item.dataset.label}: —`;
                  return `${item.dataset.label}: ${fmtOne(v)} gCO₂e/mi`;
                },
              },
            },
          },
          scales: {
            y: {
              title: { display: true, text: 'gCO₂e per mile' },
              beginAtZero: true,
            },
            x: { title: { display: true, text: 'Year' } },
          },
        },
      });
      return;
    }

    const stackedYAxis = {
      stacked: true as const,
      beginAtZero: true,
      title: { display: true, text: 'Registered vehicles' },
      ticks: {
        callback: (v: string | number) => {
          const n = Number(v);
          if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
          if (n >= 1e3) return `${(n / 1e3).toFixed(0)}k`;
          return String(v);
        },
      },
    };

    if (chartView === 'by_class') {
      const stacks = classStacksOverTime(bundle.rows, classMode);
      const keys = Array.from(new Set(stacks.flatMap((s) => Object.keys(s.byKey)))).sort((a, b) => {
        if (classMode === 'class') {
          const order = ['LDV', 'HDV'];
          return (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b));
        }
        const order = ['P', 'T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'MC', 'MH', 'BS', 'BT', 'B'];
        return (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b));
      });
      const labelFn =
        classMode === 'class'
          ? (k: string) => (k === 'LDV' ? 'Light-duty (LDV)' : k === 'HDV' ? 'Heavy-duty (HDV)' : k)
          : vehicleCategoryLabel;

      chartRef.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: stacks.map((s) => String(s.year)),
          datasets: keys.map((key, i) => ({
            label: labelFn(key),
            data: stacks.map((s) => s.byKey[key] || 0),
            backgroundColor:
              classMode === 'class' ? CLASS_COLORS[key] || categoryColor(key, i) : categoryColor(key, i),
            stack: 'type',
          })),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: standardLegend,
            title: standardTitle(
              classMode === 'class'
                ? 'Registered fleet by duty class'
                : 'Registered fleet by vehicle type'
            ),
            tooltip: {
              callbacks: {
                label: (item) => `${item.dataset.label}: ${fmtInt(Number(item.parsed.y) || 0)}`,
              },
            },
          },
          scales: {
            x: { stacked: true, title: { display: true, text: 'Year' } },
            y: stackedYAxis,
          },
        },
      });
      return;
    }

    // by_fuel stacked bar
    const stacks = fuelStacksOverTime(bundle.rows, {
      classFilter: fuelScope === 'LDV' ? 'LDV' : null,
    });
    const fuelKeys = Array.from(
      new Set(stacks.flatMap((s) => Object.keys(s.byFuel)))
    ).sort((a, b) => {
      const order = ['Gasoline', 'Diesel', 'PluginHybrid', 'Electric', 'Natural Gas', 'Hydrogen'];
      return (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b));
    });

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: stacks.map((s) => String(s.year)),
        datasets: fuelKeys.map((fuel) => ({
          label: fuelLabel(fuel),
          data: stacks.map((s) => s.byFuel[fuel] || 0),
          backgroundColor: FUEL_COLORS[fuel] || '#94a3b8',
          stack: 'fuel',
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: standardLegend,
          title: standardTitle(
            fuelScope === 'LDV'
              ? 'Light-duty fleet by fuel type'
              : 'Registered fleet by fuel type'
          ),
          tooltip: {
            callbacks: {
              label: (item) => `${item.dataset.label}: ${fmtInt(Number(item.parsed.y) || 0)}`,
            },
          },
        },
        scales: {
          x: { stacked: true, title: { display: true, text: 'Year' } },
          y: stackedYAxis,
        },
      },
    });
  }, [bundle, chartView, classMode, fuelScope, location]);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, []);

  const downloadCsv = () => {
    if (!bundle) return;
    if (chartView === 'intensity') {
      const caMap = new Map(bundle.ldvIntensityCalifornia.map((d) => [d.year, d.value]));
      const rows = bundle.ldvIntensityLocation.map((d) => ({
        year: d.year,
        location_gco2e_per_mile: Number(d.value.toFixed(2)),
        california_gco2e_per_mile: caMap.has(d.year) ? Number(caMap.get(d.year)!.toFixed(2)) : '',
      }));
      downloadDataAsCSV(rows, formatFilename(location, 'Fleet_LDV_Intensity'));
      return;
    }
    if (chartView === 'by_class') {
      const stacks = classStacksOverTime(bundle.rows, classMode);
      const keys = Array.from(new Set(stacks.flatMap((s) => Object.keys(s.byKey))));
      downloadDataAsCSV(
        stacks.map((s) => {
          const row: Record<string, number | string> = { year: s.year, total: Math.round(s.total) };
          keys.forEach((k) => {
            row[k] = Math.round(s.byKey[k] || 0);
          });
          return row;
        }),
        formatFilename(location, `Fleet_Mix_By_Type_${classMode}`)
      );
      return;
    }
    const stacks = fuelStacksOverTime(bundle.rows, {
      classFilter: fuelScope === 'LDV' ? 'LDV' : null,
    });
    const fuels = Array.from(new Set(stacks.flatMap((s) => Object.keys(s.byFuel))));
    downloadDataAsCSV(
      stacks.map((s) => {
        const row: Record<string, number | string> = { year: s.year, total: Math.round(s.total) };
        fuels.forEach((f) => {
          row[f] = Math.round(s.byFuel[f] || 0);
        });
        return row;
      }),
      formatFilename(location, `Fleet_By_Fuel_${fuelScope}`)
    );
  };

  const downloadPng = () => {
    const canvas = document.querySelector('#fleet-composition-chart canvas') as HTMLCanvasElement | null;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = formatFilename(location, `Fleet_Composition_${chartView}`) + '.png';
    link.href = canvas.toDataURL();
    link.click();
  };

  return (
    <ChartLayout
      title="Vehicle Fleet Composition"
      description="Registered vehicle mix and light-duty carbon intensity from the CARB EMFAC Fleet Database (DMV registrations)."
      chartType={chartView === 'intensity' ? 'line' : 'bar'}
      chartSelector={{
        options: [
          { value: 'by_fuel', label: 'Mix by fuel' },
          { value: 'by_class', label: 'Mix by type' },
          { value: 'intensity', label: 'LDV gCO₂e/mile' },
        ],
        value: chartView,
        onChange: (v) => setChartView(v as FleetCompositionChartView),
      }}
      additionalButtonGroups={
        chartView === 'by_class'
          ? [
              {
                label: 'Group',
                options: [
                  { value: 'vehicle_category', label: 'Category' },
                  { value: 'class', label: 'LDV / HDV' },
                ],
                value: classMode,
                onChange: (v) => setClassMode(v as 'vehicle_category' | 'class'),
              },
            ]
          : chartView === 'by_fuel'
            ? [
                {
                  label: 'Scope',
                  options: [
                    { value: 'LDV', label: 'Light-duty' },
                    { value: 'all', label: 'All vehicles' },
                  ],
                  value: fuelScope,
                  onChange: (v) => setFuelScope(v as 'all' | 'LDV'),
                },
              ]
            : undefined
      }
      keyMetrics={fleetKeyMetrics ?? undefined}
      chartContent={
        <div id="fleet-composition-chart" style={{ width: '100%' }}>
          {loading && <div style={{ padding: 24, color: '#64748b' }}>Loading fleet data…</div>}
          {error && <div style={{ padding: 24, color: '#dc2626' }}>{error}</div>}
          {!loading && !error && !bundle && (
            <div style={{ padding: 24, color: '#64748b' }}>
              No CARB Fleet Database rows found for {location}.
            </div>
          )}
          {!loading && !error && bundle && (
            <div style={{ position: 'relative', height: 420, width: '100%' }}>
              <canvas ref={canvasRef} />
            </div>
          )}
        </div>
      }
      narrativeViewId={narrativeViewId}
      overviewContent={
        <OverviewContent
          title="Overview"
          description={
            authoredBundle?.overview ||
            fleetCompositionOverviewDescription(chartView, fuelScope, classMode)
          }
        >
          <p>{keyInsightsText}</p>
        </OverviewContent>
      }
      relevanceContent={<AuthoredRelevancePanel bundle={authoredBundle} />}
      equityContent={<AuthoredEquityPanel bundle={authoredBundle} />}
      methodologyContent={
        (() => {
          const structured = fleetCompositionStructuredMethodology();
          const authoredM = undefined;
          const props = resolveMethodologyContentProps(authoredM ?? structured, {
            title: 'Methodology',
            description: 'CARB EMFAC Fleet Database registration-derived vehicle population.',
            dataSources: structured.primarySources,
            methodology: structured.calculation,
          });
          return <MethodologyContent {...props} />;
        })()
      }
      activeView={activePanel}
      onViewChange={setActivePanel}
      showRelevanceEquity={true}
      enableComments={true}
      location={location}
      appendLocationToTitle={true}
      overviewSectionId="transportation"
      overviewChartId="fleet_composition"
      onDownloadPNG={downloadPng}
      onDownloadCSV={downloadCsv}
      pngFilename={formatFilename(location, `Fleet_Composition_${chartView}`)}
      csvFilename={formatFilename(location, `Fleet_Composition_${chartView}_Data`)}
      registerPng={false}
    />
  );
};

export default FleetCompositionSection;
